import {
  generateExcelHeaders,
  parseMealDataExcel
} from '@app/utilities/create-meal-helper';
import { Upload } from 'lucide-react';
import React, { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useDispatch, useSelector } from 'react-redux';
import { getCreateLoading } from '../redux/selector';
import { bulk_create_meal, bulk_update_meal } from '../redux/reducer';

const BulkUpload = ({ isOpen, onClose, meals }) => {
  const [type, setType] = useState('add');
  const loading = useSelector(getCreateLoading);
  const dispatch = useDispatch();
  const fileRef = useRef();

  const handleFileSelect = () => {
    fileRef.current?.click();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const droppedFile = e.dataTransfer.files?.[0];

    if (!droppedFile) return;

    const data = await parseMealDataExcel(droppedFile);

    await callMeal(data);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    const data = await parseMealDataExcel(file);

    await callMeal(data);
  };

  const cleanMeal = (meal) => {
    const cleaned = { ...meal };
    if (!cleaned.video_url) delete cleaned.video_url;
    if (!cleaned.notification_message) delete cleaned.notification_message;
    if (!cleaned.lu_tips) delete cleaned.lu_tips;
    if (!cleaned.health_conditions?.length) delete cleaned.health_conditions;
    if (!cleaned.categories?.length) delete cleaned.categories;
    return cleaned;
  };

  const callMeal = async (data) => {
    if (type === 'add') {
      dispatch(bulk_create_meal({ meals: data.map(cleanMeal), onClose }));
    } else {
      const payload = data.map((meal) => {
        const cleanedMeal = cleanMeal(meal);
        delete cleanedMeal.id;
        return { result: cleanedMeal, id: meal.id };
      });
      dispatch(bulk_update_meal({ payload, onClose }));
    }

    if (fileRef.current) {
      fileRef.current.value = ''; // Reset the input value
    }
    onClose();
  };

  const saveTemplate = () => {
    const rows = generateExcelHeaders(type, meals);
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Meals');
    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array'
    });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
    });
    const fileName = `${type}-meal-template.xlsx`;
    saveAs(blob, fileName);
  };

  return (
    <div>
      {isOpen && (
        <div
          className={`transition-opacity duration-500 ease-in-out flex fixed inset-0 justify-center items-center z-[100] ${
            isOpen ? 'block opacitblock' : 'hidden opacity-0'
          }`}
        >
          <div onClick={onClose} className="fixed inset-0 backdrop-blur-md" />
          <div className="z-[120] mx-5 relative border border-[#CCCCCC] bg-white flex flex-col space-y-4 rounded-[20px] max-h-[710px] overflow-y-auto p-7 w-full max-w-[400px] shadow-[0px_0px_4px_1px_#0000001A_0px_0px_2px_1px_#FFFFFF1A_inset] pb-7">
            <p>Upload Document</p>

            <div className="flex gap-2 items-center">
              <button
                onClick={() => setType('add')}
                className={`flex-1 border py-2.5 rounded-md cursor-pointer transition-all duration-500 ease-in-out ${
                  type === 'add' ? 'border-green-600' : 'border-0'
                }`}
              >
                Add
              </button>
              <button
                onClick={() => setType('update')}
                className={`flex-1 border py-2.5 rounded-md cursor-pointer transition-all duration-500 ease-in-out ${
                  type === 'update' ? 'border-green-600' : 'border-0'
                }`}
              >
                Update
              </button>
            </div>

            {loading ? (
              <div className="grid place-items-center min-h-[20vh]  rounded-xl">
                <div>
                  <p className="text-lg font-semibold text-center">
                    Uploading File...
                  </p>
                  <p className="text-sm text-center text-gray-500">
                    This might take a while
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xlsx,.csv"
                  className="hidden"
                  onChange={handleFileChange}
                />

                <div
                  className="grid place-items-center p-4 rounded-lg border border-gray-500 border-dashed"
                  onDragOver={handleDragOver}
                  onClick={handleFileSelect}
                  onDrop={handleDrop}
                >
                  <div className="flex flex-col justify-center items-center space-y-4">
                    <div className="grid place-items-center rounded-full size-10 bg-green-500/10">
                      <Upload size={20} className="text-green-600" />
                    </div>
                    <p>Choose a file or drag and drop here</p>
                    <button className="bg-[#003333] text-white text-sm px-4 py-2.5 rounded-md">
                      Browse Files
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div>
              <p className="text-xs text-gray-500">
                {type === 'add'
                  ? 'A blank template will be downloaded. You can fill it with your own data'
                  : 'The downloaded template will contain the current data.'}
              </p>
              <div className="flex justify-end mt-3">
                <button
                  onClick={saveTemplate}
                  className="bg-[#003333] rounded-md py-2.5 text-white font-medium px-5 cursor-pointer"
                >
                  Download template
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkUpload;
